import * as React from 'react'

// Enhanced breakpoints with better mobile support
const BREAKPOINTS = {
  xs: 360,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
}

interface MobileState {
  isMobile: boolean
  isMenuOpen: boolean
  isMobileDevice: boolean
  isAndroid: boolean
  isIOS: boolean
  viewportWidth: number
  viewportHeight: number
  isLandscape: boolean
}

interface MobileContext extends MobileState {
  toggleMenu: () => void
  closeMenu: () => void
  setViewportSize: () => void
}

const MobileContext = React.createContext<MobileContext | undefined>(undefined)

export function MobileProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<MobileState>({
    isMobile: false,
    isMenuOpen: false,
    isMobileDevice: false,
    isAndroid: false,
    isIOS: false,
    viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
    viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
    isLandscape:
      typeof window !== 'undefined'
        ? window.matchMedia('(orientation: landscape)').matches
        : false
  })

  const updateViewportProperties = React.useCallback(() => {
    // Enhanced viewport height calculation
    const vh = window.innerHeight * 0.01
    document.documentElement.style.setProperty('--vh', `${vh}px`)

    // Update orientation
    const isLandscape = window.matchMedia('(orientation: landscape)').matches

    // Calculate effective viewport size
    const width = window.innerWidth
    const height = window.innerHeight
    const isMobile = width < BREAKPOINTS.md

    setState((prev) => ({
      ...prev,
      viewportWidth: width,
      viewportHeight: height,
      isMobile,
      isLandscape
    }))
  }, [])

  React.useEffect(() => {
    // Enhanced device detection with WebView support
    const detectDevice = () => {
      const ua = navigator.userAgent.toLowerCase()
      const isMobileDevice =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          ua
        )
      const isAndroid = /android/i.test(ua)
      const isIOS = /iphone|ipad|ipod/i.test(ua)
      const isWebView = /wv|webview/.test(ua)

      setState((prev) => ({
        ...prev,
        isMobileDevice,
        isAndroid,
        isIOS,
        isMobile: window.innerWidth < BREAKPOINTS.md
      }))

      // Apply platform-specific styles
      document.documentElement.classList.toggle('android', isAndroid)
      document.documentElement.classList.toggle('ios', isIOS)
      document.documentElement.classList.toggle('webview', isWebView)
    }

    // Initial setup
    detectDevice()
    updateViewportProperties()

    // Enhanced event listeners
    const resizeObserver = new ResizeObserver(updateViewportProperties)
    resizeObserver.observe(document.documentElement)

    window.addEventListener('orientationchange', () => {
      // Wait for orientation change to complete
      setTimeout(updateViewportProperties, 100)
    })

    // Handle Android keyboard visibility
    if (state.isAndroid) {
      window.visualViewport?.addEventListener(
        'resize',
        updateViewportProperties
      )
    }

    return () => {
      resizeObserver.disconnect()
      window.visualViewport?.removeEventListener(
        'resize',
        updateViewportProperties
      )
    }
  }, [updateViewportProperties, state.isAndroid])

  // Enhanced menu handling
  const toggleMenu = React.useCallback(() => {
    setState((prev) => {
      const newState = { ...prev, isMenuOpen: !prev.isMenuOpen }
      // Prevent body scroll when menu is open
      document.body.style.overflow = newState.isMenuOpen ? 'hidden' : ''
      return newState
    })
  }, [])

  const closeMenu = React.useCallback(() => {
    setState((prev) => {
      if (prev.isMenuOpen) {
        document.body.style.overflow = ''
        return { ...prev, isMenuOpen: false }
      }
      return prev
    })
  }, [])

  const setViewportSize = React.useCallback(() => {
    updateViewportProperties()
  }, [updateViewportProperties])

  return (
    <MobileContext.Provider
      value={{
        ...state,
        toggleMenu,
        closeMenu,
        setViewportSize
      }}>
      {children}
    </MobileContext.Provider>
  )
}

export function useMobile() {
  const context = React.useContext(MobileContext)
  if (!context) {
    throw new Error('useMobile must be used within a MobileProvider')
  }
  return context
}
